import { useQuery } from "@tanstack/react-query";
import { Bar, BarChart, CartesianGrid, Legend, XAxis, YAxis } from "recharts";
import { CalendarCheck, CalendarX, Files, Prohibit, User, UsersThree } from "@phosphor-icons/react";
import type { DateRangeValue } from "@/components/admin/DateRangePicker";
import {
  ChartContainer,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { throwIfNotOk } from "@/lib/api-error";
import type { OperationsReportResponse } from "./types";
import { ErrorCard, LoadingState, Metric, shortDate } from "./report-shared";

const chartConfig = {
  completed: { label: "Završeno", color: "var(--chart-1)" },
  scheduled: { label: "Zakazano", color: "var(--chart-2)" },
  cancelled: { label: "Otkazano", color: "var(--chart-3)" },
  noShow: { label: "No-show", color: "var(--chart-4)" },
} satisfies ChartConfig;

export function OperationsReportBody({ dateRange }: { dateRange: DateRangeValue }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "reports", "operations", dateRange.from, dateRange.to],
    queryFn: async () => {
      const params = new URLSearchParams({ from: dateRange.from, to: dateRange.to });
      const res = await fetch(`/api/admin/reports/operations?${params}`);
      await throwIfNotOk(res, "Greška pri učitavanju operativnog izvještaja");
      return res.json() as Promise<OperationsReportResponse>;
    },
  });

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorCard message={(error as Error).message} />;
  if (!data) return null;

  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <Metric
          tone="primary"
          icon={Files}
          label="Ukupno sesija"
          value={String(data.totalSessions)}
          detail="U odabranom periodu"
        />
        <Metric
          icon={CalendarCheck}
          label="Završeno"
          value={String(data.completed)}
          detail="Realizovane sesije"
        />
        <Metric
          icon={CalendarX}
          label="Otkazano"
          value={String(data.cancelled)}
          detail="Otkazane sesije"
        />
        <Metric
          icon={Prohibit}
          label="No-show"
          value={String(data.noShow)}
          detail="Klijent se nije pojavio"
        />
        <Metric
          icon={User}
          label="Aktivni klijenti"
          value={String(data.activeClients)}
          detail="Različiti klijenti"
        />
        <Metric
          icon={UsersThree}
          label="Aktivni terapeuti"
          value={String(data.activeTherapists)}
          detail="Različiti terapeuti"
        />
      </div>

      <ChartContainer config={chartConfig} className="h-[320px] w-full">
        <BarChart accessibilityLayer data={data.series} margin={{ left: 4, right: 8, top: 8 }}>
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="date"
            tickLine={false}
            axisLine={false}
            tickMargin={10}
            minTickGap={24}
            tickFormatter={shortDate}
          />
          <YAxis tickLine={false} axisLine={false} tickMargin={8} width={40} allowDecimals={false} />
          <ChartTooltip
            content={
              <ChartTooltipContent
                indicator="dot"
                labelFormatter={(value) => shortDate(String(value))}
                formatter={(value, name) => (
                  <div className="flex min-w-36 items-center justify-between gap-4">
                    <span className="text-muted-foreground">
                      {chartConfig[String(name) as keyof typeof chartConfig]?.label ?? name}
                    </span>
                    <span className="font-mono font-medium tabular-nums text-foreground">
                      {Number(value)}
                    </span>
                  </div>
                )}
              />
            }
          />
          <Legend content={<ChartLegendContent />} />
          <Bar dataKey="completed" stackId="s" fill="var(--color-completed)" radius={[0, 0, 0, 0]} />
          <Bar dataKey="scheduled" stackId="s" fill="var(--color-scheduled)" radius={[0, 0, 0, 0]} />
          <Bar dataKey="cancelled" stackId="s" fill="var(--color-cancelled)" radius={[0, 0, 0, 0]} />
          <Bar dataKey="noShow" stackId="s" fill="var(--color-noShow)" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ChartContainer>
    </>
  );
}
